import { reportError } from '@ms/yammer-telemetry';
import { acquireToken } from '@ms/yammer-web-authenticators';

import { getClaimsFromHeader } from './caeParseChallenges';
import { ApiError } from './errors';
import { getResourcesOrScopes } from './getResourcesOrScopes';
import { applyMiddleware } from './middleware';
import { performanceReportedApiRequest } from './performanceReportedApiRequest';
import type { ManagedApiPerformanceMetadata, ManagedApiRequest } from './types';

export const apiAction = async <TResponse>(
  request: ManagedApiRequest,
  performanceMetadata?: ManagedApiPerformanceMetadata
): Promise<TResponse> => {
  try {
    if (request.shouldSkipTokenCheck !== true) {
      await ensureTokens(request);
    }
    const response = await makeApiRequest<TResponse>(request, performanceMetadata);

    return response;
  } catch (error) {
    if (!isRetriableError(error)) {
      throw error;
    }

    reportError({ error });

    if (error instanceof ApiError && error.wwwAuthenticate) {
      const claims = getClaimsFromHeader(error.wwwAuthenticate);

      return refreshTokensAndRetry<TResponse>(request, claims);
    }

    return refreshTokensAndRetry<TResponse>(request);
  }
};

const ensureTokens = async (request: ManagedApiRequest, claims?: string): Promise<void> => {
  const resourcesOrScopes = getResourcesOrScopes(request);

  await acquireToken({ scopes: resourcesOrScopes, claims });
};

const isRetriableError = (error: unknown) => {
  const status = (error as ApiError).status;
  const isUnauthorizedError = status === 401;
  const isUnknownError = status == null;

  return isUnauthorizedError || isUnknownError;
};

const refreshTokensAndRetry = async <TResponse>(request: ManagedApiRequest, claims?: string): Promise<TResponse> => {
  if (request.shouldSkipTokenCheck !== true) {
    await ensureTokens(request, claims);
  }

  return makeApiRequest<TResponse>(request);
};

const makeApiRequest = async <TResponse>(
  request: ManagedApiRequest,
  performanceMetadata?: ManagedApiPerformanceMetadata
): Promise<TResponse> => {
  const finalRequest = await applyMiddleware(request);
  const { body } = await performanceReportedApiRequest<TResponse>(finalRequest, performanceMetadata);

  return body;
};
